import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { Option } from '../shared/types';
import { format, isValid, parse, parseISO } from 'date-fns';

export const mapWithValueType = (options: Option[]): Option[] => {
  return options.map((field) => {
    if (field.children) {
      return {
        ...field,
        children: mapWithValueType(field.children),
      };
    }

    return {
      ...field,
      // Stores the value_type in the Item's value along with the id
      // so we know how to process the CustomField later
      id: `${field.id}__${field.value_type}`,
    };
  }) as Option[];
};

export const filterByTerm = (items: Item[], term: string) => {
  if (!term) {
    return items;
  }

  return items.filter((item) =>
    item.label.toLowerCase().includes(term.toLowerCase())
  );
};

export const parseDate = (
  val: string | null,
  userDateFormat: string | null
) => {
  if (!val || !userDateFormat) {
    return null;
  }

  return parse(val, userDateFormat, new Date());
};

export const formatDate = (
  date: Date | null,
  userDateFormat: string | null
) => {
  if (!date || !userDateFormat) {
    return null;
  }

  return format(date, userDateFormat);
};

const parseDateString = (dateString: string, userDateFormat: string): Date => {
  const parsed = parse(dateString, userDateFormat, new Date());
  if (isValid(parsed)) {
    return parsed;
  }
  return parseISO(dateString);
};

const shortDisplayFormat = (userDateFormat: string): string => {
  switch (userDateFormat) {
    case 'dd/MM/yyyy':
      return 'd LLL yyyy';
    case 'yyyy/MM/dd':
      return 'yyyy LLL d';
    default: // MM/dd/yyyy
      return 'LLL d, yyyy';
  }
};

const longDisplayFormat = (userDateFormat: string): string => {
  switch (userDateFormat) {
    case 'dd/MM/yyyy':
      return 'd LLLL yyyy';
    case 'yyyy/MM/dd':
      return 'yyyy LLLL d';
    default: // MM/dd/yyyy
      return 'LLLL d, yyyy';
  }
};

export const formatShortDate = (
  dateString: string | null,
  userDateFormat: string | null
): string | null => {
  if (!dateString || !userDateFormat) {
    return null;
  }

  try {
    const date = parseDateString(dateString, userDateFormat);
    return format(date, shortDisplayFormat(userDateFormat));
  } catch (error) {
    return dateString;
  }
};

export const formatLongDate = (
  dateString: string | null,
  userDateFormat: string | null
): string | null => {
  if (!dateString || !userDateFormat) {
    return null;
  }

  try {
    const date = parseDateString(dateString, userDateFormat);
    return format(date, longDisplayFormat(userDateFormat));
  } catch (error) {
    return dateString;
  }
};
